import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import {
  LightingControl,
  NewLightingControl,
  UpdateLightingControl,
  validateControlData,
} from '@/types/lighting';
import { Database } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';

// Type aliases for cleaner code
type DbRow = Database['public']['Tables']['lighting_controls']['Row'];
type DbInsert = Database['public']['Tables']['lighting_controls']['Insert'];
type DbUpdate = Database['public']['Tables']['lighting_controls']['Update'];

// Type cast for Supabase table name
const TABLE_NAME = 'lighting_controls' as const;

/**
 * Create a new lighting control
 */
export async function createLightingControl(
  controlData: NewLightingControl,
): Promise<{ data: LightingControl | null; error: Error | null }> {
  const supabase = await getAuthenticatedSupabaseClient();
  try {
    // Validate and normalize the data
    logger.log('Validating control data:', controlData);
    const validatedData = validateControlData(controlData);
    logger.log('Validated control data:', validatedData);

    // Calculate sequence number for centralized keypads
    let sequenceNumber = null;
    if (validatedData.type === 'centralized-keypad') {
      try {
        // First, get the project_id for the floor plan
        const { data: floorPlanData, error: floorPlanError } = await supabase
          .from('floor_plans')
          .select('project_id')
          .eq('id', validatedData.floorPlanId)
          .single();

        if (floorPlanError) throw floorPlanError;

        if (!floorPlanData || !floorPlanData.project_id) {
          throw new Error(`Could not find project_id for floor plan: ${validatedData.floorPlanId}`);
        }

        const projectId = floorPlanData.project_id;
        logger.log(`Found project_id: ${projectId} for floor plan: ${validatedData.floorPlanId}`);

        // Get all floor plans in this project
        const { data: projectFloorPlans, error: projectFloorPlansError } = await supabase
          .from('floor_plans')
          .select('id')
          .eq('project_id', projectId);

        if (projectFloorPlansError) throw projectFloorPlansError;

        if (!projectFloorPlans || projectFloorPlans.length === 0) {
          throw new Error(`Could not find floor plans for project: ${projectId}`);
        }

        const floorPlanIds = projectFloorPlans.map(fp => fp.id);
        logger.log(`Found ${floorPlanIds.length} floor plans for project: ${projectId}`);

        // Get sequence numbers from all centralized keypads in THIS PROJECT only
        const { data: allSequenceNumbers, error: sequenceError } = await supabase
          .from(TABLE_NAME)
          .select('sequence_number')
          .eq('type', 'centralized-keypad')
          .in('floor_plan_id', floorPlanIds)
          .order('sequence_number', { ascending: true });

        if (sequenceError) throw sequenceError;

        if (allSequenceNumbers && allSequenceNumbers.length > 0) {
          // Extract all sequence numbers
          const usedNumbers = allSequenceNumbers
            .map(item => item.sequence_number)
            .filter(num => num !== null);

          logger.log('Used sequence numbers in this project:', usedNumbers);

          // Find the first gap in sequence starting from 1
          let foundGap = false;
          for (let i = 1; i <= usedNumbers.length + 1; i++) {
            if (!usedNumbers.includes(i)) {
              sequenceNumber = i;
              foundGap = true;
              logger.log(`Found gap at sequence number ${i}, using it for new keypad`);
              break;
            }
          }

          // If no gap was found, use the next available number
          if (!foundGap) {
            sequenceNumber = Math.max(...usedNumbers) + 1;
            logger.log(`No gaps found, using next available number: ${sequenceNumber}`);
          }
        } else {
          // No existing keypads in this project, start at 1
          sequenceNumber = 1;
          logger.log('No existing keypads in this project, starting at sequence number 1');
        }
      } catch (sequenceError) {
        logger.error('Error determining sequence number:', sequenceError);
        // Fallback to simple increment based on project
        try {
          // Get the project_id for the floor plan
          const { data: floorPlanData } = await supabase
            .from('floor_plans')
            .select('project_id')
            .eq('id', validatedData.floorPlanId)
            .single();

          if (floorPlanData?.project_id) {
            // Get all floor plans in this project
            const { data: projectFloorPlans } = await supabase
              .from('floor_plans')
              .select('id')
              .eq('project_id', floorPlanData.project_id);

            if (projectFloorPlans && projectFloorPlans.length > 0) {
              const floorPlanIds = projectFloorPlans.map(fp => fp.id);

              // Get maximum sequence number for this project
              const { data: maxSequenceData } = await supabase
                .from(TABLE_NAME)
                .select('sequence_number')
                .eq('type', 'centralized-keypad')
                .in('floor_plan_id', floorPlanIds)
                .order('sequence_number', { ascending: false })
                .limit(1);

              sequenceNumber =
                maxSequenceData && maxSequenceData.length > 0 && maxSequenceData[0].sequence_number
                  ? maxSequenceData[0].sequence_number + 1
                  : 1;

              logger.log(
                `Fallback - using sequence number for centralized keypad in this project: ${sequenceNumber}`,
              );
            } else {
              // No floor plans found, start at 1
              sequenceNumber = 1;
            }
          } else {
            // Couldn't get project info, start at 1
            sequenceNumber = 1;
          }
        } catch (fallbackError) {
          logger.error('Error in fallback sequence number calculation:', fallbackError);
          sequenceNumber = 1; // ultimate fallback
        }
      }

      logger.log(`Using sequence number for centralized keypad: ${sequenceNumber}`);
    }

    // Check for default scale if not provided
    let finalScale = 1.0; // Default scale
    if (validatedData.type) {
      try {
        // Import here to avoid circular dependencies
        const elementSizeDefaults = await import('@/services/elementSizeDefaultsService');

        // Try to get default scale for this control type
        const defaultScale = await elementSizeDefaults.getDefaultScale(
          'control',
          validatedData.type,
          undefined, // Controls don't have subcategories
        );

        if (defaultScale) {
          finalScale = defaultScale;
        }
      } catch (error) {
        // If fetching defaults fails, just log and continue without defaults
        logger.error('[createLightingControl] Failed to fetch default scale:', error);
      }
    }

    // Convert to DB format
    const dbInsertData: DbInsert = {
      name: validatedData.name,
      type: validatedData.type,
      assigned_load: validatedData.assignedLoad || null,
      circuit_id: validatedData.circuitId || null,
      floor_plan_id: validatedData.floorPlanId,
      number_of_buttons: validatedData.numberOfButtons,
      labels: validatedData.labels as any, // JSON conversion
      actions: validatedData.actions as any, // JSON conversion
      x_position: validatedData.xPosition,
      y_position: validatedData.yPosition,
      sequence_number: sequenceNumber,
      room_id: validatedData.roomId || null,
      scale: finalScale, // Apply default scale
    };

    logger.log('DB insert data:', dbInsertData);
    logger.log('Labels being saved:', JSON.stringify(validatedData.labels));
    logger.log('Room ID being saved:', dbInsertData.room_id);

    // Insert into database
    const { data, error } = await supabase.from(TABLE_NAME).insert(dbInsertData).select().single();

    if (error) {
      logger.error('Supabase error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
      });
      throw error;
    }

    // Transform database record to frontend model
    const result = transformDbToModel(data);
    return { data: result, error: null };
  } catch (error) {
    logger.error('Error creating lighting control:', error);
    if (error && typeof error === 'object' && 'message' in error) {
      logger.error('Error message:', (error as any).message);
    }
    if (error && typeof error === 'object' && 'code' in error) {
      logger.error('Error code:', (error as any).code);
    }
    if (error && typeof error === 'object' && 'details' in error) {
      logger.error('Error details:', (error as any).details);
    }
    return { data: null, error: error as Error };
  }
}

/**
 * Get a lighting control by ID
 */
export async function getLightingControl(
  id: string,
): Promise<{ data: LightingControl | null; error: Error | null }> {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase.from(TABLE_NAME).select().eq('id', id).single();

    if (error) throw error;

    return { data: transformDbToModel(data), error: null };
  } catch (error) {
    logger.error('Error fetching lighting control:', error);
    return { data: null, error: error as Error };
  }
}

/**
 * Get all lighting controls for a floor plan
 */
export async function getLightingControlsByFloorPlan(
  floorPlanId: string,
): Promise<{ data: LightingControl[]; error: Error | null }> {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select()
      .eq('floor_plan_id', floorPlanId);

    if (error) throw error;

    return {
      data: data?.map(transformDbToModel) || [],
      error: null,
    };
  } catch (error) {
    logger.error('Error fetching lighting controls:', error);
    return { data: [], error: error as Error };
  }
}

/**
 * Update a lighting control
 */
export async function updateLightingControl(
  id: string,
  updates: UpdateLightingControl,
): Promise<{ data: LightingControl | null; error: Error | null }> {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    // Need to get current state to properly validate
    const { data: current } = await getLightingControl(id);
    if (!current) {
      throw new Error(`Control with ID ${id} not found`);
    }

    // Create a merged version to validate
    const mergedData = {
      ...current,
      ...updates,
      // Ensure we don't accidentally override these
      id: current.id,
      createdAt: current.createdAt,
      updatedAt: current.updatedAt,
    };

    // Only validate if number of buttons is changing
    const dataToUpdate =
      updates.numberOfButtons !== undefined ? validateControlData(mergedData) : updates;

    // Transform to database format
    const dbUpdates: DbUpdate = transformModelToDb(dataToUpdate);

    // Update in database
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data: transformDbToModel(data), error: null };
  } catch (error) {
    logger.error('Error updating lighting control:', error);
    return { data: null, error: error as Error };
  }
}

/**
 * Delete a lighting control
 */
export async function deleteLightingControl(
  id: string,
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const { error } = await supabase.from(TABLE_NAME).delete().eq('id', id);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    logger.error('Error deleting lighting control:', error);
    return { success: false, error: error as Error };
  }
}

/**
 * Helper to transform database record to model
 */
function transformDbToModel(record: DbRow): LightingControl {
  if (!record) return null;

  return {
    id: record.id,
    name: record.name,
    type: record.type,
    assignedLoad: record.assigned_load,
    circuitId: record.circuit_id,
    floorPlanId: record.floor_plan_id,
    numberOfButtons: record.number_of_buttons,
    labels: (record.labels as string[]) || [],
    actions: (record.actions as any) || [],
    xPosition: record.x_position,
    yPosition: record.y_position,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
    sequenceNumber: record.sequence_number,
    roomId: record.room_id,
    productId: record.product_id,
    scale: record.scale || 1.0,
    showProductImage: record.show_product_image,
    productImageX: record.product_image_x,
    productImageY: record.product_image_y,
    productImageWidth: record.product_image_width,
    productImageHeight: record.product_image_height,
  };
}

/**
 * Helper to transform model to database format
 */
function transformModelToDb(model: Partial<LightingControl>): DbUpdate {
  const dbRecord: DbUpdate = {};

  if (model.name !== undefined) dbRecord.name = model.name;
  if (model.type !== undefined) dbRecord.type = model.type;
  if (model.assignedLoad !== undefined) dbRecord.assigned_load = model.assignedLoad;
  if (model.circuitId !== undefined) dbRecord.circuit_id = model.circuitId;
  if (model.floorPlanId !== undefined) dbRecord.floor_plan_id = model.floorPlanId;
  if (model.numberOfButtons !== undefined) dbRecord.number_of_buttons = model.numberOfButtons;
  if (model.labels !== undefined) dbRecord.labels = model.labels as any;
  if (model.actions !== undefined) dbRecord.actions = model.actions as any;
  if (model.xPosition !== undefined) dbRecord.x_position = model.xPosition;
  if (model.yPosition !== undefined) dbRecord.y_position = model.yPosition;
  if (model.sequenceNumber !== undefined) dbRecord.sequence_number = model.sequenceNumber;
  if (model.roomId !== undefined) dbRecord.room_id = model.roomId;
  if (model.productId !== undefined) dbRecord.product_id = model.productId;
  if (model.scale !== undefined) dbRecord.scale = model.scale;
  if (model.showProductImage !== undefined) dbRecord.show_product_image = model.showProductImage;
  if (model.productImageX !== undefined) dbRecord.product_image_x = model.productImageX;
  if (model.productImageY !== undefined) dbRecord.product_image_y = model.productImageY;
  if (model.productImageWidth !== undefined) dbRecord.product_image_width = model.productImageWidth;
  if (model.productImageHeight !== undefined)
    dbRecord.product_image_height = model.productImageHeight;

  return dbRecord;
}

/**
 * Update control position
 */
export async function updateControlPosition(
  id: string,
  x: number,
  y: number,
): Promise<LightingControl | null> {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({ x_position: x, y_position: y })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return transformDbToModel(data);
  } catch (error) {
    logger.error('Error updating control position:', error);
    return null;
  }
}
